<script lang="ts">
  /**
   * One post, its comments nested Skool-style around whatever is being answered, and
   * the reply box in the right place. The BODY of a thread reader — never its chrome.
   *
   * THREE COPIES BECAME THIS. The Helper pushed a full-width screen, the moderation
   * page opened a drawer, the timeline opened a read-only drawer; the substance was
   * the same in all three and 85 lines of it were character-for-character identical.
   * Not boilerplate either — the remount key, the load effect, the `targetInList`
   * fallback and the whole `<SkoolPost>` prop block, which is exactly the code where a
   * fix applied to one copy and not the others goes unnoticed.
   *
   * WHAT STAYS WITH THE HOST, and why it is not a prop here:
   *
   *   * CHROME. A `<Drawer>` and a pushed screen with a Back button are different
   *     containers, not different configurations of one. The host renders its own and
   *     puts this inside it — which is why there is no `header` snippet: a snippet
   *     would only be the host's markup routed through here for no gain.
   *   * THE COMPOSER, because it differs in substance: the panel sends straight to
   *     Skool with mention candidates, the dashboard queues to the outbox, and the
   *     timeline has none at all. It is a snippet, and it RECEIVES THE THREAD, so a
   *     host can read the participants it needs without this component knowing what
   *     a mention is.
   *
   * `transform` is the one hook for a host that knows something this does not: the
   * Helper merges comments it has posted but not yet mirrored, so the reply the owner
   * just wrote is present on the first paint rather than two round trips later.
   */
  import CommentThread from './CommentThread.svelte';
  import SkoolPost from './SkoolPost.svelte';
  import StateBlock from './StateBlock.svelte';
  import { fetchPostThread } from '@api';
  import type { PostDetail } from '@api';

  export interface ThreadTarget {
    kind: 'post' | 'comment';
    /** The row being answered — a post id or a comment id. */
    id: number;
    /** The post the thread belongs to. For a post target this is its own id. */
    postId: number;
  }

  let {
    skoolId,
    target,
    detail: given = undefined,
    compact = false,
    collapseComments = false,
    now = Date.now(),
    transform,
    errorText,
    composer,
    onLike = null,
    likedIds = null,
    deletedIds = null,
    onLoaded,
    waitingIds = null,
  }: {
    skoolId: number | null;
    target: ThreadTarget | null;
    /**
     * The thread, already loaded, for a host that fetched it itself.
     *
     * The timeline holds a cache of opened posts and pages the drawer between them, so
     * it hands the thread over rather than having this refetch what it already has.
     * When given, nothing here fetches and `loading`/`error` are the host's to report.
     */
    detail?: PostDetail | null | undefined;
    /** The 340px panel's density — same meaning as on `<SkoolPost>`. */
    compact?: boolean;
    /**
     * Start with the conversation folded behind a "N replies" toggle.
     *
     * OPT-IN, and default `false` on purpose. The three surfaces that had this
     * component first — the moderation deck, the timeline's drawer and the Helper —
     * all exist to READ the conversation, and folding it there would hide the thing
     * the reader opened. The weekly digest is the opposite: it lists several threads
     * as evidence for one theme, and a 30-comment thread is a wall between a reader
     * and the next piece of evidence.
     *
     * The targeted comment overrides it: a thread opened to show one specific reply
     * cannot start with that reply hidden, so it expands.
     */
    collapseComments?: boolean;
    now?: number;
    /** A host's chance to fold in what only it knows, before anything renders. */
    transform?: ((detail: PostDetail) => Promise<PostDetail> | PostDetail) | undefined;
    /**
     * What a failure should SAY here, if the host has an opinion.
     *
     * The two apps genuinely differ: the dashboard navigates away on a 401 and wants
     * nothing rendered, the panel stays put and turns a 403 into "you don't have
     * access to this community". Return a string to show it, null to show nothing
     * (the host took over), or omit the prop for the plain message.
     */
    errorText?: ((err: unknown) => string | null) | undefined;
    /**
     * The reply box, rendered under whichever row is being answered.
     *
     * Receives the thread AND the active target, because the target moves: opening a
     * row answers that row, and every other post and comment offers "Reply" to move
     * the box to it. A host that sends must send to the second argument, not to
     * whatever the thread was opened with.
     */
    composer?: import('svelte').Snippet<[PostDetail, ThreadTarget]> | undefined;
    /**
     * Like the post, or one comment in it — the host performs the write, because only it
     * knows whether this browser has a Skool session.
     *
     * It takes the SKOOL id, not ours: the vote endpoint is addressed by Skool's own
     * external id and the thread already carries one for the post and every comment, so
     * a host would otherwise have to look up something it is already holding.
     */
    onLike?: ((target: { kind: 'post' | 'comment'; id: number; externalId: string }) => Promise<void> | void) | null;
    /**
     * Skool ids the owner has already liked, so the thumb opens in the right state.
     *
     * A HOST CAPABILITY, like `onLike` itself: only a surface with the owner's Skool
     * session can know this. Our mirror stores `upvotes` and no per-viewer vote, on
     * purpose — the scrape runs as a different Skool account, so a stored one would be
     * the scraper's. Null therefore means "not known here", not "not liked".
     */
    likedIds?: Set<string> | null;
    /**
     * Skool ids this browser has just found to 404 — the post's own, or any comment's.
     *
     * AN OVERLAY, not the source. The rows already carry `deleted_at` from the backend
     * and that is what makes the dashboard work at all: it holds no Skool session and
     * can never discover a deletion itself. This is the same ⊕ every other domain here
     * uses — mirror truth, corrected by what this browser learned more recently — and
     * it is what closes the gap between the Helper seeing the 404 and the ingest
     * landing. Null means "nothing extra known here", never "not deleted".
     */
    deletedIds?: Set<string> | null;
    /** The thread, once it is in. The inbox uses it to put the rows it still has to
     *  ask about into the order they are actually shown in. */
    onLoaded?: ((detail: PostDetail) => void) | undefined;
    /** Comments still waiting on somebody — everything else folds. See
     *  `<CommentThread>`; omit it and the whole thread renders. */
    waitingIds?: number[] | null | undefined;
  } = $props();

  let fetched = $state<PostDetail | null>(null);
  /**
   * The BACKEND's thread, before `transform` touched it. Never rendered.
   *
   * Kept so `restage()` can re-derive from the source instead of from its own last
   * output. Re-transforming the transformed copy would bake each pass's corrections
   * into the thing the next pass corrects, and a patch that is baked in can never be
   * taken back off — so a staged value the mirror had since disagreed with would
   * survive forever, and the panel would go on showing an upvote count nothing
   * believed any more. `DmInbox.repatchList` documents the same rule for its list;
   * this is that rule, for this card.
   */
  let raw: PostDetail | null = null;
  let loading = $state(false);
  let error = $state<string | null>(null);
  /** The host's, when it has one; ours otherwise. */
  const detail = $derived(given !== undefined ? given : fetched);
  const hostOwnsIt = $derived(given !== undefined);

  /**
   * (Re)load when a different THREAD is opened, and not otherwise.
   *
   * Keyed on the post, because the post is what `load()` fetches. Moving the target
   * to another comment of the same thread is a move WITHIN what is already on screen
   * — the inbox walks down a post's flagged comments one at a time — and re-reading
   * the same thread to answer the next one would blank the card and re-fetch for a
   * result identical to what it just threw away.
   */
  let loadedFor = $state<string | null>(null);
  /** Which thread `fetched` actually holds, so a re-read of the one on screen can
   *  be told apart from a move to a different one. `loadedFor` cannot answer that —
   *  it is set on intent, this is set on arrival. */
  let shownFor = $state<string | null>(null);
  $effect(() => {
    if (hostOwnsIt || !target || skoolId == null) {
      loadedFor = null;
      return;
    }
    const key = `${skoolId}:${target.postId}`;
    if (loadedFor === key) return;
    loadedFor = key;
    void load();
  });

  /** Re-read the thread. Exported so a host with its own signal — the Helper repaints
   *  when a staged reply lands in storage — can ask without remounting. */
  export async function reload(): Promise<void> {
    await load();
  }

  /**
   * Re-run `transform` over the thread already in hand. NO NETWORK.
   *
   * The counterpart to `reload()`, for the signal that says "this browser learned
   * something the backend has not" rather than "the backend moved". Everything that
   * signal carries is already in local storage, so the host's transform can apply it
   * without asking anyone: re-fetching would be a round trip to be told what we hold,
   * and — because `load()` is the only other way in — it would also blank the card.
   *
   * Silent when there is nothing fetched yet, or when the host owns the detail: both
   * mean there is no copy here to re-derive, and the host's own data is not ours to
   * re-run a transform over.
   */
  export async function restage(): Promise<void> {
    if (hostOwnsIt || !raw || !transform) return;
    try {
      fetched = await transform(raw);
    } catch {
      // The overlay is a correction, not the source. Leaving the last good render
      // in place is strictly better than blanking a card over a failed re-merge.
    }
  }

  async function load(): Promise<void> {
    const t = target;
    const sid = skoolId;
    if (hostOwnsIt || !t || sid == null) return;
    loading = true;
    error = null;
    /**
     * BLANK ONLY WHEN THE THREAD IS ACTUALLY CHANGING.
     *
     * `fetched = null` unconditionally made every refresh a flicker: the card
     * emptied, the reader lost their place, and the composer under it remounted —
     * on a signal that in the common case resolved to the same rows it had. A
     * different post genuinely has nothing to show yet and should blank; a re-read
     * of the one on screen should hold the last good render until the new one lands.
     */
    const key = `${sid}:${t.postId}`;
    // Against `shownFor`, NOT `loadedFor`: the effect above sets `loadedFor` to the
    // new key before it calls in here, so comparing to it is always a match and
    // would never blank anything. `shownFor` is what is actually on screen.
    if (shownFor !== key) fetched = null;
    try {
      const fresh = await fetchPostThread(sid, t.postId);
      raw = fresh;
      fetched = transform ? await transform(fresh) : fresh;
      shownFor = key;
      onLoaded?.(fetched);
    } catch (err: unknown) {
      error = errorText
        ? errorText(err)
        : err instanceof Error
          ? err.message
          : 'Failed to load the thread.';
    } finally {
      loading = false;
    }
  }

  /**
   * Is the comment being answered actually IN the thread we fetched?
   *
   * It is not always: a reply deep in a collapsed subtree, or one the mirror has and
   * the detail route paged past. When it is missing the composer has nothing to sit
   * under, and without this it silently does not render at all — the owner opens a row
   * to answer it and finds no reply box.
   */
  /**
   * A comment's like, resolved to the Skool id the vote endpoint wants.
   *
   * The thread has the id; the comment list below deals in ours, because that is what a
   * reply target is. Refusing when it is missing is the honest outcome — a comment the
   * detail route did not carry an `external_id` for cannot be voted on, and silently
   * doing nothing would look like a failed like.
   */
  async function likeComment(id: number): Promise<void> {
    const externalId = detail?.comments.find((c) => c.id === id)?.external_id;
    if (!onLike || !externalId) throw new Error('this comment cannot be liked');
    await onLike({ kind: 'comment', id, externalId });
  }

  /**
   * Which row the composer is currently under.
   *
   * Seeded from what was clicked — opening a mention answers that mention, which is
   * the point of the inbox — and moved by the Reply affordance on any other row.
   * Cleared whenever a different thread opens, so a stale choice cannot survive into
   * a thread it does not belong to.
   */
  let active = $state<ThreadTarget | null>(null);
  $effect(() => {
    void target?.kind;
    void target?.id;
    active = null;
  });
  const replyingTo = $derived<ThreadTarget | null>(active ?? target);

  /** Backend truth ⊕ what this browser just learned — the same test the rows render by. */
  // `deletedAt` is spelled out rather than declared optional with `?`: an optional
  // parameter in a value-level function parses in svelte-check but FAILS `vite build`.
  const isGone = (externalId: string | null | undefined, deletedAt: string | null | undefined): boolean =>
    !!deletedAt || (!!externalId && !!deletedIds?.has(externalId));

  /**
   * Is the thing being answered gone from Skool?
   *
   * HIDING THE "Reply" LINK IS NOT ENOUGH. The deck walks the queue and sets `target`
   * itself, so a deleted row becomes the active one without anybody clicking anything
   * — which is exactly how a composer ended up open on a deleted comment with the
   * badge sitting right above it.
   *
   * A reply here cannot be filed: the row's own id is what `parent_id` would carry,
   * and Skool no longer has it. Letting somebody write the answer and fail on send is
   * the worse order of events.
   */
  const replyingToDeleted = $derived.by(() => {
    const t = replyingTo;
    const d = detail;
    if (!t || !d) return false;
    if (t.kind === 'post') return isGone(d.post.external_id, d.post.deleted_at);
    const c = d.comments.find((x) => x.id === t.id);
    return c ? isGone(c.external_id, c.deleted_at) : false;
  });

  const targetCommentId = $derived(target?.kind === 'comment' ? target.id : null);

  /** Folded only while the caller asked for it, nobody has opened it, and there is no
   *  targeted comment to show — a thread opened AT a reply must not start with that
   *  reply hidden. */
  let userOpened = $state(false);
  const commentsOpen = $derived(!collapseComments || userOpened || targetCommentId != null);
  const targetInList = $derived(
    !!detail && targetCommentId != null && detail.comments.some((c) => c.id === targetCommentId),
  );
</script>

{#if target || detail}
  {#if loading && !detail}
    <StateBlock state="loading" compact />
  {:else if error && !detail}
    <StateBlock state="error" compact message={error} onretry={() => void load()} />
  {:else if detail}
    <SkoolPost
      {compact}
      author={detail.post.author}
      title={detail.post.title}
      body={detail.post.content}
      images={detail.post.images}
      videos={detail.post.videos}
      skoolUrl={detail.post.url}
      upvotes={detail.post.upvotes}
      commentCount={detail.post.comment_count}
      createdAt={detail.post.created_at}
      pinned={detail.post.pinned}
      clamp={0}
      showHandle
      onReply={composer ? () => (active = { kind: 'post', id: detail.post.id, postId: detail.post.id }) : null}
      liked={!!detail.post.external_id && !!likedIds?.has(detail.post.external_id)}
      deleted={!!detail.post.deleted_at || (!!detail.post.external_id && !!deletedIds?.has(detail.post.external_id))}
      onLike={onLike && detail.post.external_id
        ? () => onLike({ kind: 'post', id: detail.post.id, externalId: detail.post.external_id })
        : null}
      replyActive={replyingTo?.kind === 'post'}
      {now}
    />

    {#if replyingTo?.kind === 'post' && composer && replyingToDeleted}
      <div class="mt-4">{@render goneNotice('post')}</div>
    {:else if replyingTo?.kind === 'post' && composer}
      <div class="mt-4">{@render composer(detail, replyingTo)}</div>
    {/if}

    <!-- ONE RULE, AND IT EARNS ITS PLACE: where the post stops and the conversation
         starts. Skool draws exactly this line and nothing else in a thread, and without
         it the post's last paragraph and the first comment ran together. The count is a
         heading for a list, so with no list it is a label on a sentence that already
         says the same thing. -->
    {#if detail.comments.length}
      <!-- No "N COMMENTS" heading. The post's own action row already carries that
           number, and the rule is what the heading was really there to do. -->
      <div class="{compact ? 'mb-5 mt-6' : 'mb-7 mt-8'} border-t hair"></div>
      {#if !commentsOpen}
        <button
          class="eyebrow w-full rounded-btn py-1.5 text-[0.62rem] text-base-content/50 hover:bg-base-200 hover:text-base-content"
          onclick={() => (userOpened = true)}
        >
          Show {detail.comments.length}
          {detail.comments.length === 1 ? 'reply' : 'replies'}
        </button>
      {/if}
    {/if}
    {#if detail.comments.length && commentsOpen}
      <!-- Comments nest under their true parent (by parent_id), so a reply always sits
           beneath the comment it answers — immune to the API's chronological order. The
           targeted comment is highlighted and the composer renders directly beneath it. -->
      <CommentThread
        {compact}
        comments={detail.comments}
        targetId={targetCommentId}
        {waitingIds}
        activeId={replyingTo?.kind === 'comment' ? replyingTo.id : null}
        onReply={composer ? (id) => (active = { kind: 'comment', id, postId: detail.post.id }) : null}
        onLike={onLike ? (id) => likeComment(id) : null}
        {likedIds}
        {deletedIds}
        composer={replyingTo?.kind === 'comment' && composer ? threadComposer : null}
        {now}
      />
    {/if}
    <!-- Nothing at all when there are no comments. A rule and a sentence announcing an
         empty list is two pieces of furniture around a fact the empty space states by
         itself — and on a post with no replies the reply box is already the only thing
         left on the card. -->

    <!-- The fallback the note on `targetInList` is about: the comment being answered
         is not in the thread we fetched, so the box has nothing to sit under. -->
    {#if replyingTo?.kind === 'comment' && replyingTo.id === targetCommentId && !targetInList && composer}
      <div class="mt-4">
        {#if replyingToDeleted}{@render goneNotice('comment')}{:else}{@render composer(detail, replyingTo)}{/if}
      </div>
    {/if}
  {/if}
{/if}

{#snippet threadComposer()}
  {#if detail && composer && replyingTo}
    <div class="mt-3.5">
      {#if replyingToDeleted}{@render goneNotice('comment')}{:else}{@render composer(detail, replyingTo)}{/if}
    </div>
  {/if}
{/snippet}

<!--
  What replaces the reply box, rather than nothing at all.
  ───────────────────────────────────────────────────────
  An empty space where the composer was reads as a bug — the reader has just walked
  onto this row expecting to answer it. This says why there is nothing to write in, and
  names the one action still available, which is to clear the row and move on.
-->
{#snippet goneNotice(kind: 'post' | 'comment')}
  <p class="rounded-box bg-error/[0.06] px-3 py-2 text-xs leading-relaxed text-base-content/75 ring-1 ring-inset ring-error/15">
    This {kind} was deleted on Skool, so there is nothing left to reply to. Mark it
    answered to take it off the queue.
  </p>
{/snippet}
